import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useComparison } from '../../hooks/useComparison';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import CategoryHero from './components/CategoryHero';
import CategoryFilter from '../../components/ui/CategoryFilter';
import VendorGrid from './components/VendorGrid';
import FeaturedCarousel from './components/FeaturedCarousel';
import ComparisonTool from './components/ComparisonTool';
import ComparisonBar from '../../components/ui/ComparisonBar';
import Icon from '../../components/AppIcon';
import vendorDataService from '../../services/vendorDataService';
import searchService from '../../services/searchService';


const CategoryBrowse = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFilters, setSelectedFilters] = useState({});
  const [vendors, setVendors] = useState([]);
  const [featuredVendors, setFeaturedVendors] = useState([]);
  const [recentlyAddedVendors, setRecentlyAddedVendors] = useState([]);
  const [mostBookedVendors, setMostBookedVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('popularity');
  const [hasMore, setHasMore] = useState(true);
  const [availableFilters, setAvailableFilters] = useState({});

  // Comparison functionality
  const {
    selectedVendors,
    showComparison,
    toggleComparison,
    clearComparison,
    openComparison,
    closeComparison,
    isSelected,
    canAddMore,
    canCompare
  } = useComparison(3);

  // Get category, subcategory, and search from URL params
  const category = searchParams.get('category') || 'photography';
  const subcategory = searchParams.get('subcategory') || '';
  const initialSearch = searchParams.get('search') || '';

  useEffect(() => {
    // Load vendors from Supabase
    const loadVendors = async () => {
      setLoading(true);
      try {
        // Build filters from selected filters and category
        const filters = {
          ...selectedFilters,
          category: category !== 'all' ? category : undefined,
          sortBy: sortBy === 'popularity' ? 'total_bookings' :
            sortBy === 'rating' ? 'average_rating' :
              sortBy === 'price-low' ? 'starting_price' : 'starting_price',
          sortOrder: sortBy === 'price-high' ? 'desc' : 'asc',
          limit: 50
        };

        // Fetch vendors
        const result = await vendorDataService.getVendors(filters);

        if (result.success) {
          // Transform vendors to match component expectations
          const transformedVendors = result.data.map(vendor => ({
            id: vendor.id,
            name: vendor.business_name,
            culturalSpecialization: vendor.cultural_specialties?.[0] || vendor.category,
            image: vendor.vendor_gallery?.[0]?.image_url || 'https://images.unsplash.com/photo-1606216794074-735e91aa2c92',
            rating: vendor.average_rating || 0,
            reviewCount: vendor.review_count || 0,
            location: `${vendor.city}, ${vendor.state}`,
            distance: null,
            services: vendor.services || [],
            startingPrice: vendor.starting_price,
            priceRange: vendor.price_range,
            isVerified: vendor.is_verified,
            isFeatured: vendor.is_featured,
            recentBookings: vendor.total_bookings || 0
          }));

          setVendors(transformedVendors);
          setFeaturedVendors(transformedVendors.filter(v => v.isFeatured));

          // Calculate dynamic filter options from vendors
          calculateAvailableFilters(transformedVendors);
        }
      } catch (error) {
        console.error('Error loading vendors:', error);
      } finally {
        setLoading(false);
      }
    };

    loadVendors();
  }, [category, selectedFilters, sortBy]);

  // Load recently added and most booked vendors
  useEffect(() => {
    const loadSpecialVendors = async () => {
      try {
        // Fetch recently added vendors (sorted by created_at desc)
        const recentResult = await vendorDataService.getVendors({
          category: category !== 'all' ? category : undefined,
          sortBy: 'created_at',
          sortOrder: 'desc',
          limit: 6
        });

        if (recentResult.success) {
          const transformedRecent = recentResult.data.map(vendor => ({
            id: vendor.id,
            name: vendor.business_name,
            culturalSpecialization: vendor.cultural_specialties?.[0] || vendor.category,
            image: vendor.vendor_gallery?.[0]?.image_url || 'https://images.unsplash.com/photo-1606216794074-735e91aa2c92',
            rating: vendor.average_rating || 0,
            reviewCount: vendor.review_count || 0,
            location: `${vendor.city}, ${vendor.state}`,
            services: vendor.services || [],
            startingPrice: vendor.starting_price,
            isVerified: vendor.is_verified,
            isFeatured: vendor.is_featured,
            recentBookings: vendor.total_bookings || 0
          }));
          setRecentlyAddedVendors(transformedRecent);
        }

        // Fetch most booked vendors
        const bookedResult = await vendorDataService.getVendors({
          category: category !== 'all' ? category : undefined,
          sortBy: 'total_bookings',
          sortOrder: 'desc',
          limit: 6
        });

        if (bookedResult.success) {
          const transformedBooked = bookedResult.data.map(vendor => ({
            id: vendor.id,
            name: vendor.business_name,
            culturalSpecialization: vendor.cultural_specialties?.[0] || vendor.category,
            image: vendor.vendor_gallery?.[0]?.image_url || 'https://images.unsplash.com/photo-1606216794074-735e91aa2c92',
            rating: vendor.average_rating || 0,
            reviewCount: vendor.review_count || 0,
            location: `${vendor.city}, ${vendor.state}`,
            services: vendor.services || [],
            startingPrice: vendor.starting_price,
            isVerified: vendor.is_verified,
            isFeatured: vendor.is_featured,
            recentBookings: vendor.total_bookings || 0
          }));
          setMostBookedVendors(transformedBooked);
        }
      } catch (error) {
        console.error('Error loading special vendors:', error);
      }
    };

    loadSpecialVendors();
  }, [category]);

  // Calculate available filters dynamically from vendors
  const calculateAvailableFilters = (vendorList) => {
    const locationCounts = {};
    const priceCounts = { budget: 0, 'mid-range': 0, premium: 0, luxury: 0 };
    const culturalCounts = {};

    vendorList.forEach(vendor => {
      // Count locations
      const location = vendor.location?.split(',')[1]?.trim(); // Get state
      if (location) {
        const locationKey = location.toLowerCase().replace(/\s+/g, '-');
        locationCounts[locationKey] = (locationCounts[locationKey] || 0) + 1;
      }

      // Count price ranges
      const price = vendor.startingPrice || 0;
      if (price < 2000) priceCounts.budget++;
      else if (price < 5000) priceCounts['mid-range']++;
      else if (price < 10000) priceCounts.premium++;
      else priceCounts.luxury++;

      // Count cultural specializations
      if (vendor.culturalSpecialization) {
        const culturalKey = vendor.culturalSpecialization.toLowerCase().replace(/\s+/g, '-');
        culturalCounts[culturalKey] = (culturalCounts[culturalKey] || 0) + 1;
      }
    });

    // Build filter options
    const filters = {
      location: Object.entries(locationCounts).map(([key, count]) => ({
        id: key,
        label: key.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
        cultural: 'USA',
        count
      })).sort((a, b) => b.count - a.count).slice(0, 10),

      price: [
        { id: 'budget', label: 'Budget Friendly', cultural: 'Under $2,000', count: priceCounts.budget },
        { id: 'mid-range', label: 'Mid Range', cultural: '$2,000 - $5,000', count: priceCounts['mid-range'] },
        { id: 'premium', label: 'Premium', cultural: '$5,000 - $10,000', count: priceCounts.premium },
        { id: 'luxury', label: 'Luxury', cultural: '$10,000+', count: priceCounts.luxury }
      ].filter(p => p.count > 0),

      cultural: Object.entries(culturalCounts).map(([key, count]) => ({
        id: key,
        label: key.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
        cultural: 'Specialization',
        count
      })).sort((a, b) => b.count - a.count).slice(0, 10)
    };

    setAvailableFilters(filters);
  };

  // Handle initial search from URL
  useEffect(() => {
    if (initialSearch) {
      handleSearch(initialSearch);
    }
  }, []); // Only run on mount

  const handleSearch = async (query) => {
    if (!query || query.trim().length === 0) return;

    setLoading(true);
    try {
      const result = await searchService.searchVendors(query, { category, ...selectedFilters });

      if (result.success) {
        const transformedVendors = result.data.map(vendor => ({
          id: vendor.id,
          name: vendor.business_name,
          culturalSpecialization: vendor.cultural_specialties?.[0] || vendor.category,
          image: vendor.vendor_gallery?.[0]?.image_url || 'https://images.unsplash.com/photo-1606216794074-735e91aa2c92',
          rating: vendor.average_rating || 0,
          reviewCount: vendor.review_count || 0,
          location: `${vendor.city}, ${vendor.state}`,
          distance: null,
          services: vendor.services || [],
          startingPrice: vendor.starting_price,
          priceRange: vendor.price_range,
          isVerified: vendor.is_verified,
          isFeatured: vendor.is_featured,
          recentBookings: vendor.total_bookings || 0
        }));

        setVendors(transformedVendors);
      }
    } catch (error) {
      console.error('Error searching vendors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filters) => {
    setSelectedFilters(filters);
    // Update URL params
    const params = new URLSearchParams(searchParams);
    Object.entries(filters).forEach(([key, values]) => {
      if (values && values.length > 0) {
        params.set(key, values.join(','));
      } else {
        params.delete(key);
      }
    });
    setSearchParams(params);
  };

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
    const params = new URLSearchParams(searchParams);
    params.set('sort', newSortBy);
    setSearchParams(params);
  };

  const handleLoadMore = async () => {
    // Simulate loading more vendors
    await new Promise(resolve => setTimeout(resolve, 1000));
    setHasMore(false);
  };

  const handleMessage = (vendorId) => {
    console.log('Message vendor:', vendorId);
    // Implement messaging logic
  };

  const handleCheckAvailability = (vendorId) => {
    console.log('Check availability for vendor:', vendorId);
    // Implement availability check logic
  };

  const handleToggleFavorite = (vendorId, isFavorited) => {
    console.log('Toggle favorite for vendor:', vendorId, isFavorited);
    // Update vendor favorite status
    setVendors(prevVendors =>
      prevVendors.map(vendor =>
        vendor.id === vendorId ? { ...vendor, isFavorited } : vendor
      )
    );
  };

  const handleBookNow = (vendorId) => {
    console.log('Book vendor:', vendorId);
    // Implement booking logic
  };

  const handleClearFilters = () => {
    setSelectedFilters({});
    const params = new URLSearchParams();
    params.set('category', category);
    setSearchParams(params);
  };

  const handleRemoveFromComparison = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    if (vendor) {
      toggleComparison(vendor);
    }
  };

  const getFilteredVendors = () => {
    let filtered = [...vendors];

    // Apply filters
    if (selectedFilters.category && selectedFilters.category.length > 0) {
      // Filter by category logic
    }

    if (selectedFilters.location && selectedFilters.location.length > 0) {
      // Filter by location logic
    }

    if (selectedFilters.price && selectedFilters.price.length > 0) {
      // Filter by price logic
    }

    if (selectedFilters.cultural && selectedFilters.cultural.length > 0) {
      // Filter by cultural specialization logic
    }

    // Apply sorting
    switch (sortBy) {
      case 'rating':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      case 'distance':
        filtered.sort((a, b) => a.distance - b.distance);
        break;
      case 'price-low':
        filtered.sort((a, b) => a.startingPrice - b.startingPrice);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.startingPrice - a.startingPrice);
        break;
      case 'newest':
        // Sort by newest (mock implementation)
        break;
      case 'availability':
        filtered.sort((a, b) => (b.isAvailable ? 1 : 0) - (a.isAvailable ? 1 : 0));
        break;
      default: // popularity
        filtered.sort((a, b) => b.recentBookings - a.recentBookings);
    }

    return filtered;
  };

  const filteredVendors = getFilteredVendors();

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero Section */}
      <CategoryHero category={category} onSearch={handleSearch} />

      {/* Filters */}
      <CategoryFilter
        variant="mobile"
        selectedFilters={selectedFilters}
        onFilterChange={handleFilterChange}
        availableFilters={availableFilters}
        resultCount={filteredVendors.length}
        onClearAll={handleClearFilters}
      />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="lg:flex lg:space-x-8">
          {/* Desktop Sidebar Filters */}
          <CategoryFilter
            variant="sidebar"
            selectedFilters={selectedFilters}
            onFilterChange={handleFilterChange}
            availableFilters={availableFilters}
            resultCount={filteredVendors.length}
            onClearAll={handleClearFilters}
          />

          {/* Main Content Area */}
          <div className="flex-1 space-y-12">
            {/* Featured Vendors Carousel */}
            {featuredVendors.length > 0 && (
              <FeaturedCarousel
                title="Top Rated in Category"
                subtitle="Highly recommended vendors with excellent reviews"
                vendors={featuredVendors}
                onMessage={handleMessage}
                onCheckAvailability={handleCheckAvailability}
                onToggleFavorite={handleToggleFavorite}
                viewAllLink="/category-browse"
              />
            )}

            {/* Recently Added Carousel */}
            {recentlyAddedVendors.length > 0 && (
              <FeaturedCarousel
                title="Recently Added"
                subtitle="New vendors joining our platform"
                vendors={recentlyAddedVendors}
                onMessage={handleMessage}
                onCheckAvailability={handleCheckAvailability}
                onToggleFavorite={handleToggleFavorite}
                viewAllLink="/category-browse"
              />
            )}

            {/* Most Booked Carousel */}
            {mostBookedVendors.length > 0 && (
              <FeaturedCarousel
                title="Most Booked This Month"
                subtitle="Popular choices among couples"
                vendors={mostBookedVendors}
                onMessage={handleMessage}
                onCheckAvailability={handleCheckAvailability}
                onToggleFavorite={handleToggleFavorite}
                viewAllLink="/category-browse"
              />
            )}

            {/* All Vendors Grid */}
            <div className="bg-white rounded-xl shadow-soft p-6">
              <VendorGrid
                vendors={filteredVendors}
                loading={loading}
                hasMore={hasMore}
                onLoadMore={handleLoadMore}
                onMessage={handleMessage}
                onCheckAvailability={handleCheckAvailability}
                onToggleFavorite={handleToggleFavorite}
                onToggleComparison={toggleComparison}
                isSelectedForComparison={isSelected}
                canAddToComparison={canAddMore}
                sortBy={sortBy}
                onSortChange={handleSortChange}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Tool Modal */}
      {showComparison && (
        <ComparisonTool
          vendors={selectedVendors}
          onClose={closeComparison}
          onMessage={handleMessage}
          onBookNow={handleBookNow}
        />
      )}

      {/* Comparison Bar */}
      <ComparisonBar
        selectedVendors={selectedVendors}
        maxComparisons={3}
        onRemoveVendor={handleRemoveFromComparison}
        onClearAll={clearComparison}
        onCompare={openComparison}
        onClose={clearComparison}
      />

      {/* Educational Content Section */}
      <div className="bg-gradient-to-br from-primary-50 to-secondary-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="font-heading font-bold text-3xl text-text-primary">
              Understanding South Asian Wedding Traditions
            </h2>
            <p className="text-lg text-text-secondary leading-relaxed">
              Each vendor type plays a crucial role in creating authentic South Asian wedding celebrations.
              From the sacred Mandap setup to the joyous Dhol beats during Baraat, every element carries
              deep cultural significance that connects generations and honors traditions.
            </p>

            <div className="grid md:grid-cols-3 gap-8 mt-12">
              <div className="bg-white rounded-xl p-6 shadow-soft">
                <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Icon name="Heart" size={24} color="#8B4513" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                  Cultural Authenticity
                </h3>
                <p className="text-text-secondary font-caption">
                  Our vendors understand the nuances of different South Asian traditions and regional customs.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-soft">
                <div className="w-12 h-12 bg-secondary-50 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Icon name="Users" size={24} color="#DAA520" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                  Family Involvement
                </h3>
                <p className="text-text-secondary font-caption">
                  We help coordinate with extended families to ensure everyone's traditions are honored.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-soft">
                <div className="w-12 h-12 bg-accent-50 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Icon name="Globe" size={24} color="#DC143C" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                  Modern Fusion
                </h3>
                <p className="text-text-secondary font-caption">
                  Perfect blend of traditional elements with contemporary American wedding styles.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Spacer for Mobile - Adjusted for comparison bar */}
      <div className={`${selectedVendors.length > 0 ? 'h-20' : 'h-4'} lg:h-0`} />

      <Footer />
    </div>
  );
};

export default CategoryBrowse;